import RootStore from "@stores/RootStore";
import { makeAutoObservable } from "mobx";
import { authClient } from "@services/authService";
import { Account } from "@services/demosService";
import {
  setUser as setChatwootUser,
  reset as resetChatwoot,
} from "@src/utils/chatwoot";
import { fetchChatwootIdentity } from "@services/chatwootService";
import { identifyUser, resetUser, track, EVENTS } from "@src/utils/analytics";
import { acceptInvitation, getAccounts, AccountMembership } from "@services/teamService";
import { takePendingInvite } from "@src/utils/pendingInvite";
import {
  ACTIVE_ACCOUNT_STORAGE_KEY,
  IMPERSONATE_STORAGE_KEY,
  getImpersonateUserId,
} from "@src/utils/apiAuthInterceptor";
import { api } from "@services/api";
import {
  canViewFieldsIn,
  resolveActiveAccountId,
  webMemberships,
} from "@src/utils/teamAccess";

export enum ROUTES {
  LOGIN = "/login",
  REGISTER = "/register",
  VERIFY_EMAIL = "/verify-email",
  PASSWORD_RESET = "/password-reset",
  PASSWORD_FORGOT = "/password-forgot",
  // AI assistant (ChatGPT/Claude) connection consent — the backend's OAuth server sends users here.
  OAUTH_CONSENT = "/oauth/consent",
  PROFILE = "/profile",
  SETTINGS = "/settings",
  FIELDS = "/fields",
  CROPS = "/crops",
  DEVICES = "/devices",
  TRACKS = "/tracks",
  AGENT = "/agent",
  ANALYTICS = "/analytics",
  REPORTS = "/reports",
  REPORT_VIEW = "/reports/:id",
  DEVICES_CALIBRATION = "/devices/calibration/:id",
  DEVICE_TECHNICAL = "/devices/:id/technical",
  PRESCRIPTION = "/prescription/:field_id",
  TEAM = "/team",
  ADMIN = "/green-admin-growth",
}

class AccountStore {
  public readonly rootStore: RootStore;

  account: Account | null = null;
  private setAccount = (v: Account | null) => (this.account = v);

  // The user's own account plus every account they were invited to — minus
  // those whose role is "Driver app only", which the web dashboard never offers.
  memberships: AccountMembership[] = [];
  private setMemberships = (v: AccountMembership[]) => (this.memberships = v);

  // The account currently being viewed (own id, or an owner who invited them).
  activeAccountId: string = "";
  private setActiveAccountIdInternal = (v: string) => (this.activeAccountId = v);

  initialized: boolean = false;
  private setInitialized = (v: boolean) => (this.initialized = v);

  authenticated: boolean = false;
  private setAuthenticated = (v: boolean) => (this.authenticated = v);

  // True when this tab is an admin impersonating another user (per-tab).
  impersonating: boolean = false;
  private setImpersonating = (v: boolean) => (this.impersonating = v);

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
    this.auth().then(() => this.setInitialized(true));
  }

  /** The membership entry for the account being viewed (own or an owner's). */
  get activeAccount(): AccountMembership | null {
    return (
      this.memberships.find((m) => m.accountId === this.activeAccountId) ?? null
    );
  }

  /** True when the user is viewing their own account. */
  get isViewingOwnAccount(): boolean {
    if (!this.account) return true;
    return this.activeAccountId === "" || this.activeAccountId === this.account.id;
  }

  /** True when acting as a limited member inside someone else's account. */
  get isMember(): boolean {
    return !this.isViewingOwnAccount;
  }

  // Capability getters — always true on the user's own account; otherwise from
  // the active membership's group. The backend is authoritative; these gate UI.
  get canEdit(): boolean {
    return this.isViewingOwnAccount ? true : Boolean(this.activeAccount?.canEdit);
  }
  get canDelete(): boolean {
    return this.isViewingOwnAccount ? true : Boolean(this.activeAccount?.canDelete);
  }
  get canExport(): boolean {
    return this.isViewingOwnAccount ? true : Boolean(this.activeAccount?.canExport);
  }
  get canAccessDevices(): boolean {
    return this.isViewingOwnAccount
      ? true
      : Boolean(this.activeAccount?.canAccessDevices);
  }
  /**
   * Fields, boundaries, yield maps and field analytics. A role can turn these
   * off entirely (the backend then answers 403 FIELDS_NOT_ALLOWED), so the UI
   * hides the navigation instead of showing broken screens. Missing = allowed.
   */
  get canViewFields(): boolean {
    return this.isViewingOwnAccount ? true : canViewFieldsIn(this.activeAccount);
  }

  /** Where to land when the requested screen isn't available in this account. */
  get homeRoute(): ROUTES {
    if (this.canViewFields) return ROUTES.FIELDS;
    if (this.canAccessDevices) return ROUTES.DEVICES;
    return ROUTES.SETTINGS;
  }

  // Paywall flags follow the account being viewed (the owner's flags when
  // impersonating; the member's own session flags are irrelevant there).
  get subscriptionPaid(): boolean {
    if (this.activeAccount) return this.activeAccount.subscriptionPaid;
    return this.account?.subscriptionPaid ?? false;
  }

  /**
   * Whether this user may grant access (invite, groups, reassign members).
   * Needs their OWN subscription: the backend refuses an inherited one so a
   * member of a paid account can't pass it down another level
   * (requirePaidTeamMgmt in backend routes/team.ts). `subscriptionPaid`
   * includes the inherited flag, so it showed those controls and then 403'd.
   * Falls back to it only for a backend that predates the field.
   */
  get canManageTeam(): boolean {
    const self = this.memberships.find((m) => m.isSelf);
    return self?.canManageTeam ?? this.subscriptionPaid;
  }

  get cameraInstalled(): boolean {
    if (this.activeAccount) return this.activeAccount.cameraInstalled;
    return this.account?.cameraInstalled ?? false;
  }

  /**
   * Switch the account context. Persists the choice and hard-reloads so every
   * store and screen re-fetches under the new X-Active-Account-Id header (mirrors
   * the hard redirect used by logout). No-op when already on that account.
   */
  setActiveAccountId = (id: string) => {
    if (id === this.activeAccountId) return;
    try {
      if (!this.account || id === this.account.id) {
        window.localStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
      } else {
        window.localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, id);
      }
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  /**
   * The backend refused this client inside the active account (403
   * CLIENT_NOT_ALLOWED — the role became "Driver app only" while this tab was
   * open). Fall back to the user's own account. Wired from index.tsx through
   * the fetch interceptor. Returns false during boot, where auth() below
   * already handles the same 403 for the accounts call.
   */
  handleClientNotAllowed = (): boolean => {
    if (!this.initialized || !this.account) return false;
    try {
      // Already cleared (the boot path got there first): nothing to undo.
      if (!window.localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY)) return false;
      window.localStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
    } catch {
      return false;
    }
    window.location.reload();
    return true;
  };

  /**
   * get-session with one retry. A single failed request here used to mean an
   * immediate, silent redirect to /login — indistinguishable from a real
   * expiry, and one blip (Neon wake-up, a 5xx, a dropped connection) was
   * enough. Better Auth's client never throws: failures come back as `error`.
   */
  private getSessionWithRetry = async () => {
    const first = await authClient.getSession();
    if (!first.error) return first;
    await new Promise((resolve) => setTimeout(resolve, 1500));
    return authClient.getSession();
  };

  /** Pages a signed-out visitor is expected on; "no session" there is not news. */
  private static AUTH_PAGES: string[] = [
    ROUTES.LOGIN,
    ROUTES.REGISTER,
    ROUTES.VERIFY_EMAIL,
    ROUTES.PASSWORD_RESET,
    ROUTES.PASSWORD_FORGOT,
  ];

  /**
   * Record why the app decided the user is signed out. The 401 path reports
   * from utils/sessionExpired.ts; this covers the page-load check, which is
   * where the 2026-09-03 "/fields → /login" logout came from and where PostHog
   * had nothing to say about it.
   */
  private reportSessionLost(error: { status?: number } | null | undefined) {
    const page = window.location.pathname;
    if (error) {
      track(EVENTS.SESSION_LOST, { reason: "boot-error", status: error.status ?? null, page });
    } else if (!AccountStore.AUTH_PAGES.includes(page)) {
      // A clean "no session" on an app page. Includes an anonymous visitor
      // opening a bookmark to /fields, so read it together with the person's
      // previous session rather than as an expiry on its own.
      track(EVENTS.SESSION_LOST, { reason: "boot-null", page });
    }
  }

  auth = async () => {
    try {
      const { data: session, error } = await this.getSessionWithRetry();
      if (session?.user) {
        // Admin impersonation (this tab only). The session cookie is still the
        // admin's, but every API call carries X-Impersonate-User-Id, so the
        // backend serves the impersonated user. Fetch the EFFECTIVE user from
        // /api/v1/me (which respects the header) and display that account.
        if (getImpersonateUserId()) {
          try {
            const me = await api.get<{
              id: string;
              email: string;
              name: string;
              subscriptionPaid: boolean;
              cameraInstalled: boolean;
              emailVerified: boolean;
              impersonating: boolean;
            }>("/api/v1/me");
            this.setAccount({
              id: me.id,
              email: me.email,
              name: me.name || me.email,
              subscriptionPaid: Boolean(me.subscriptionPaid),
              cameraInstalled: Boolean(me.cameraInstalled),
              emailVerified: Boolean(me.emailVerified),
            });
            this.setMemberships([]);
            this.setActiveAccountIdInternal(me.id);
            this.setImpersonating(true);
            this.setAuthenticated(true);
            return;
          } catch (e) {
            // Impersonation context is broken (e.g. target gone / not admin) —
            // clear it and fall through to the admin's own account.
            try {
              window.sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY);
            } catch {
              /* ignore */
            }
          }
        }

        const user = session.user;
        this.setAccount({
          id: user.id,
          email: user.email,
          name: user.name || user.email,
          // better-auth additionalFields; client type may not infer them, so cast.
          subscriptionPaid: Boolean((user as { subscriptionPaid?: boolean }).subscriptionPaid),
          cameraInstalled: Boolean((user as { cameraInstalled?: boolean }).cameraInstalled),
          // Carried by better-auth's session user natively; we were discarding it.
          emailVerified: Boolean((user as { emailVerified?: boolean }).emailVerified),
          phoneNumber: (user as { phoneNumber?: string | null }).phoneNumber ?? null,
          organizationName: (user as { organizationName?: string | null }).organizationName ?? null,
        });
        // Identify the user in Chatwoot with verified identity (HMAC hash
        // signed by the backend). Non-blocking; chat still works if it fails.
        void fetchChatwootIdentity().then((identity) => {
          setChatwootUser({
            id: user.id,
            name: user.name || user.email,
            email: user.email,
            identifierHash: identity?.identifier_hash,
          });
        });
        identifyUser(user.id, {
          email: user.email,
          name: user.name || user.email,
          subscriptionPaid: Boolean((user as { subscriptionPaid?: boolean }).subscriptionPaid),
        });

        // A signup that came from an emailed invitation redeems its token
        // BEFORE the account list loads (which still grants pending invitations
        // by email match — the path this replaces). Best effort: a refused or
        // failed token leaves that fallback as it was.
        const inviteToken = takePendingInvite();
        if (inviteToken) {
          try {
            await acceptInvitation(inviteToken);
          } catch (e) {
            console.warn("Invitation not accepted", e);
          }
        }

        // Load the account list (own + invited-to) and resolve the active one.
        // Failure is non-fatal — the backend enforces access regardless, and we
        // fall back to the user's own account.
        try {
          const memberships = webMemberships(await getAccounts());
          this.setMemberships(memberships);
          let stored: string | null = null;
          try {
            stored = window.localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY);
          } catch {
            /* ignore */
          }
          // Falls back to own when the stored account is gone or its role is
          // "Driver app only" (filtered out above).
          const resolved = resolveActiveAccountId(stored, user.id, memberships);
          this.setActiveAccountIdInternal(resolved);
          if (resolved === user.id) {
            try {
              window.localStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
            } catch {
              /* ignore */
            }
          }
        } catch (e) {
          // A 403 here means the stored X-Active-Account-Id names an account
          // this user can no longer reach (membership revoked, account gone).
          // That header is attached from localStorage to EVERY /api/v1 call, so
          // until it is cleared the whole app 403s — including this very call,
          // which is the one that would normally clear it. The result is a
          // lockout that survives reloads and re-logins: one production user hit
          // 186 4xx in 80 minutes on 2026-09-07, logging out and back in twice.
          //
          // Clearing the key (and retrying once, so the session recovers now
          // rather than on the next reload) is what breaks the loop. Only on
          // 403: a network blip must not knock a legitimate team member out of
          // the account they are working in.
          //
          // The same path covers 403 CLIENT_NOT_ALLOWED: the stored account's
          // role became "Driver app only", so the backend refuses the web inside
          // it (spec DRIVER_ACCESS_AND_LOAD_SYNC §3) and we land on our own.
          const status = (e as { statusCode?: number })?.statusCode;
          this.setActiveAccountIdInternal(user.id);
          if (status === 403) {
            try {
              window.localStorage.removeItem(ACTIVE_ACCOUNT_STORAGE_KEY);
            } catch {
              /* ignore */
            }
            try {
              this.setMemberships(webMemberships(await getAccounts()));
            } catch {
              this.setMemberships([]);
            }
          } else {
            this.setMemberships([]);
          }
        }

        this.setAuthenticated(true);
      } else {
        this.reportSessionLost(error);
        this.setAuthenticated(false);
      }
    } catch (e) {
      // Not the network — that comes back as `error` above. Something in the
      // post-session bookkeeping threw; still worth a trace.
      track(EVENTS.SESSION_LOST, {
        reason: "boot-exception",
        page: window.location.pathname,
        message: e instanceof Error ? e.message : String(e),
      });
      this.setAuthenticated(false);
    }
  }

  /**
   * End impersonation in this tab. Clears the per-tab marker and closes the
   * tab (it was opened by the admin panel); if the browser refuses to close a
   * script-opened tab, fall back to navigating to the admin's own fields view.
   */
  exitImpersonation = () => {
    try {
      window.sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    this.setImpersonating(false);
    window.close();
    // If still here a moment later, the tab couldn't self-close — reload into
    // the admin's own account (no impersonation header now).
    window.setTimeout(() => {
      window.location.href = ROUTES.FIELDS;
    }, 100);
  };

  logout = async () => {
    resetChatwoot();
    resetUser();
    this.setAuthenticated(false);

    try {
      await authClient.signOut();
    } catch (e) {
      console.error("Failed to logout", e);
    }

    window.location.href = ROUTES.LOGIN;
  };
}

export default AccountStore;
